/**
 * Whether what the app is showing right now is live.
 *
 * This exists because of the 2026-09-27 outage. The server was off for hours
 * and the app kept rendering, from data the service worker had kept, with
 * nothing to say so. Members reasonably concluded it was working.
 *
 * Every API caller reports what happened to its request here, and the
 * ConnectionBanner reads the result. There are three ways to learn the server
 * is gone, and all of them need handling:
 *
 * - The request fails outright (no route, DNS, device offline).
 * - The service worker answers with a saved copy of a public board. It marks
 *   those with `X-From-Cache`, because otherwise the copy is indistinguishable
 *   from a live response. See public/service-worker.js.
 * - Something in front of the server answers for it. Traefik returns a plain
 *   text 404 while the containers are starting, and 502-504 when a container
 *   is down. None of those are the API talking.
 *
 * Once anything looks wrong, a probe of `/api/health` runs every 15 seconds
 * until the server answers, so the banner clears without the member having to
 * do anything.
 */

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');
const PROBE_URL = `${API_BASE}/health`;
const PROBE_INTERVAL_MS = 15000;

export const FROM_CACHE_HEADER = 'x-from-cache';
export const CACHED_AT_HEADER = 'x-sw-cached-at';

const initialState = {
  // The API answered the last time anybody asked.
  reachable: true,
  // The browser itself says it has no network. Changes the wording, not the state.
  deviceOffline: typeof navigator !== 'undefined' && navigator.onLine === false,
  // When the oldest saved copy on screen was stored, if any are being shown.
  savedAt: null,
  // The server came back after saved data was shown. What is on screen may
  // still be the saved copy, so the member is offered a refresh.
  recovered: false
};

let state = initialState;
const listeners = new Set();
let probeTimer = null;
let probeInFlight = false;

function setState(patch) {
  const next = { ...state, ...patch };
  const changed = Object.keys(next).some((key) => next[key] !== state[key]);
  if (!changed) return;
  state = next;
  listeners.forEach((listener) => listener(state));
}

export function getConnectionStatus() {
  return state;
}

export function subscribeConnectionStatus(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function olderOf(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return new Date(a) <= new Date(b) ? a : b;
}

function startProbing() {
  if (probeTimer) return;
  probeTimer = setInterval(probe, PROBE_INTERVAL_MS);
}

function stopProbing() {
  if (!probeTimer) return;
  clearInterval(probeTimer);
  probeTimer = null;
}

export function reportLive() {
  stopProbing();
  if (state.reachable && !state.savedAt) return;
  setState({
    reachable: true,
    savedAt: null,
    recovered: state.recovered || Boolean(state.savedAt)
  });
}

export function reportUnreachable() {
  setState({ reachable: false, recovered: false });
  startProbing();
}

export function reportSaved(savedAt) {
  setState({ reachable: false, savedAt: olderOf(state.savedAt, savedAt), recovered: false });
  startProbing();
}

export function dismissRecovered() {
  setState({ recovered: false });
}

async function probe() {
  if (probeInFlight) return;
  probeInFlight = true;
  try {
    const response = await fetch(PROBE_URL, { cache: 'no-store' });
    const body = response.ok ? await response.json().catch(() => null) : null;
    if (body && body.status === 'ok') {
      reportLive();
    } else {
      reportUnreachable();
    }
  } catch (error) {
    reportUnreachable();
  } finally {
    probeInFlight = false;
  }
}

/** Ask the server directly, now or after a short delay. */
export function checkConnection(delayMs = 0) {
  if (delayMs > 0) {
    setTimeout(probe, delayMs);
  } else {
    probe();
  }
}

/**
 * Classify a response. `getHeader` takes a lower-case header name, so axios and
 * fetch callers can both use this.
 */
export function noteResponse(status, getHeader) {
  if (getHeader(FROM_CACHE_HEADER) === '1') {
    reportSaved(getHeader(CACHED_AT_HEADER));
    return;
  }
  const contentType = getHeader('content-type') || '';
  const fromTheApi = contentType.includes('application/json');
  // A 502-504 from the proxy means the server is down. The same codes in JSON are
  // the server itself answering that something it depends on failed (MatchPlay
  // not responding, an integration not configured), so it is up. Treating those
  // as an outage put the "can't reach the server" banner over a working app.
  if ((status === 502 || status === 503 || status === 504) && !fromTheApi) {
    reportUnreachable();
    return;
  }
  // The API's own 404s are JSON. A plain one came from something in front of
  // it, most likely Traefik with no route because the containers are down or
  // still starting. Ask the server rather than guessing.
  if (status === 404 && !fromTheApi) {
    checkConnection();
    return;
  }
  reportLive();
}

export function noteNetworkError() {
  reportUnreachable();
}

/** "10:31 AM" today, "Sep 26, 10:31 PM" otherwise. */
export function formatSavedAt(savedAt, now = new Date()) {
  const saved = new Date(savedAt);
  if (Number.isNaN(saved.getTime())) return null;
  const time = saved.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (saved.toDateString() === now.toDateString()) return time;
  const date = saved.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}

export function isFromCache(response) {
  return response.headers.get(FROM_CACHE_HEADER) === '1';
}

/** `fetch`, reporting the outcome here. For callers that do not use axios. */
export async function fetchWithStatus(url, init) {
  let response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    noteNetworkError();
    throw error;
  }
  noteResponse(response.status, (name) => response.headers.get(name));
  return response;
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    setState({ deviceOffline: true });
  });
  window.addEventListener('online', () => {
    setState({ deviceOffline: false });
    if (!state.reachable) checkConnection();
  });
}

/** Test hook. */
export function resetConnectionStatusForTests() {
  stopProbing();
  probeInFlight = false;
  state = { ...initialState, deviceOffline: false };
  listeners.clear();
}
